import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dumbbell,
  Video,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ClipboardList,
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';

interface AlumnoCard {
  userId: string;
  fullName: string;
  expiryDate: string | null;
  pendingVideos: number;
}

// Jugador asignado cuyo bono PF NO está vigente por fecha (sección "Caducados /
// renovar"). expiryDate = fecha de caducidad más reciente (RPC expired).
interface ExpiredCard {
  userId: string;
  fullName: string;
  expiryDate: string | null;
}

// §30: status por (alumno, día) — coherente con gym_routine_status_for_day
//   - 'green'  → completó la rutina del día.
//   - 'orange' → parcial (empezó pero no terminó).
//   - 'red'    → tenía rutina publicada y no la ejecutó.
//   - 'rest'   → rutina marcada como descanso (sin ejercicios).
//   - 'none'   → no había rutina publicada ese día (no se pinta nada).
type DayStatus = 'green' | 'orange' | 'red' | 'rest' | 'none';

interface CalendarEntry {
  userId: string;
  fullName: string;
  status: DayStatus;
}

function formatExpiry(date: string | null) {
  if (!date) return 'Sin fecha';
  return new Date(date).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}
function toDateStr(y: number, m: number, d: number) {
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}
function todayStrLocal() {
  const t = new Date();
  return toDateStr(t.getFullYear(), t.getMonth(), t.getDate());
}

// Lunes-primero: en JS getDay() devuelve 0=Dom..6=Sáb; aquí pasamos a
// 0=Lun..6=Dom para casar con el layout del grid mensual.
function dowMondayFirst(d: Date) {
  return (d.getDay() + 6) % 7;
}

type ViewKey = 'alumnos' | 'calendario' | 'revision';

// §37: estado de revisión por (alumno, día) — devuelto por la RPC
// `gym_routine_review_status_for_day` (migración 093). Solo nos
// interesan los días con rutina ejecutada por el alumno; el RPC ya
// filtra none/rest/no_session.
type ReviewStatus = 'reviewed' | 'pending';

export default function MonitorGymPage() {
  const { profile } = useAuthStore();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewKey>('alumnos');
  const [alumnos, setAlumnos] = useState<AlumnoCard[]>([]);
  // Asignados con bono PF caducado (sección aparte "renovar").
  const [caducados, setCaducados] = useState<ExpiredCard[]>([]);
  // FB-9: ids de alumnos GYM activos sin rutina publicada en las próximas 48h.
  const [unplanned48h, setUnplanned48h] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [listaIlegible, setListaIlegible] = useState(false);
  const [avisoIlegible, setAvisoIlegible] = useState(false);

  const load = useCallback(async () => {
    if (!profile) return;

    // ACTIVOS y CADUCADOS salen de dos RPC SECURITY DEFINER (E-1):
    //   - monitor_gym_alumnos(): asignados con bono PF VIGENTE por fecha
    //     (active AND activated_at IS NOT NULL AND expiry_date > now()).
    //   - monitor_gym_alumnos_expired() (mig 174): asignados con bono NO vigente
    //     (inversión exacta), con su fecha de caducidad más reciente.
    // Ambos traen expiry_date + pending_videos y filtran por asignación al
    // monitor actual server-side. Antes se hacían queries crudas por asignación
    // (que listaban también caducados) + N+1 a user_credits/gym_videos; además
    // la RLS de user_credits para el monitor es por-grupo (no gym) → devolvía
    // "Sin fecha". Estos RPC lo resuelven.
    const [activosRes, expiradosRes] = await Promise.all([
      supabase.rpc('monitor_gym_alumnos'),
      supabase.rpc('monitor_gym_alumnos_expired'),
    ]);
    const { data: activos } = activosRes;
    const { data: expirados } = expiradosRes;

    // ⚠️ Con el error tirado, la pantalla remataba con «No tienes alumnos
    //    Preparación Física asignados» y la coletilla de que el administrador
    //    debe asignártelos. A un entrenador con alumnos se le decía que no
    //    tiene ninguno y a quién reclamar.
    if (activosRes.error || expiradosRes.error) {
      setListaIlegible(true);
      setLoading(false);
      return;
    }
    setListaIlegible(false);

    // Ambos RPC comparten shape (mig 174 espeja al de activos). El tipo se anota
    // localmente porque el helper `rpc` con `Args: never` no infiere la fila.
    type GymListRow = {
      user_id: string;
      full_name: string;
      avatar_url: string | null;
      expiry_date: string | null;
      pending_videos: number;
    };

    setAlumnos(
      ((activos ?? []) as GymListRow[]).map((r) => ({
        userId: r.user_id,
        fullName: r.full_name,
        expiryDate: r.expiry_date,
        pendingVideos: r.pending_videos,
      }))
    );
    setCaducados(
      ((expirados ?? []) as GymListRow[]).map((r) => ({
        userId: r.user_id,
        fullName: r.full_name,
        expiryDate: r.expiry_date,
      }))
    );

    // FB-9: alerta de 48h. La RPC es role-aware; para un monitor devuelve
    // SOLO sus asignados GYM activos sin rutina publicada en hoy..hoy+2.
    const { data: unplanned, error: errUnplanned } = await supabase.rpc(
      'gym_players_unplanned_48h'
    );
    // Este aviso es una MARCA sobre las fichas: sin él ninguna sale marcada, y
    // no salir marcado significa «este ya lo tienes planificado».
    setAvisoIlegible(!!errUnplanned);
    setUnplanned48h(new Set(((unplanned ?? []) as { user_id: string }[]).map((u) => u.user_id)));

    setLoading(false);
  }, [profile]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#A3E635] font-oswald animate-pulse">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Dumbbell size={22} className="text-[#A3E635]" />
        <h2 className="font-oswald text-2xl uppercase tracking-wider text-white">
          Preparación Física — Mis alumnos
        </h2>
      </div>

      {/* FB-9 — aviso de jugadores sin planificar en las próximas 48h. */}
      {alumnos.length > 0 && unplanned48h.size > 0 && (
        <div
          data-testid="gym-unplanned-48h-banner"
          className="flex items-center gap-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl px-4 py-3"
        >
          <AlertCircle size={18} className="text-[#EF4444] flex-shrink-0" />
          <p className="font-inter text-sm text-white">
            <span className="font-semibold">{unplanned48h.size}</span>{' '}
            {unplanned48h.size === 1
              ? 'jugador sin sesiones planificadas en las próximas 48h.'
              : 'jugadores sin sesiones planificadas en las próximas 48h.'}{' '}
            <span className="text-text-secondary">
              Planifica sus sesiones (o márcalas como descanso).
            </span>
          </p>
        </div>
      )}

      {avisoIlegible && (
        <div className="card border-error/40 bg-error/5" data-testid="gym-aviso-48h-ilegible">
          <p className="text-error text-sm">
            No se ha podido comprobar quién tiene la planificación al día. Que una ficha no salga
            marcada no significa que la tenga.
          </p>
        </div>
      )}

      {listaIlegible ? (
        <div
          className="card flex flex-col items-center gap-3 py-16"
          data-testid="gym-lista-ilegible"
        >
          <Dumbbell size={40} className="text-error opacity-40" />
          <p className="font-inter text-sm text-error">
            No se ha podido cargar tu lista de alumnos de Preparación Física.
          </p>
          <p className="font-inter text-xs text-error opacity-80">
            Esto no significa que no tengas ninguno asignado: vuelve a intentarlo.
          </p>
        </div>
      ) : alumnos.length === 0 && caducados.length === 0 ? (
        <div className="card flex flex-col items-center gap-3 py-16 text-text-secondary">
          <Dumbbell size={40} className="opacity-20" />
          <p className="font-inter text-sm">No tienes alumnos Preparación Física asignados</p>
          <p className="font-inter text-xs opacity-60">
            El administrador debe asignarte alumnos desde el panel Preparación Física
          </p>
        </div>
      ) : (
        <>
          {alumnos.length > 0 && (
            <>
              {/* Toggle vista alumnos / calendario (§30) */}
              <div className="flex gap-1 bg-bg-surface border border-bg-border rounded-xl p-1 w-fit">
                <button
                  onClick={() => setView('alumnos')}
                  className={`px-5 py-2 rounded-lg text-sm font-inter transition-colors ${
                    view === 'alumnos'
                      ? 'bg-[#A3E635] text-bg-primary font-semibold'
                      : 'text-text-secondary hover:text-white'
                  }`}
                  data-testid="gym-view-alumnos"
                >
                  Mis alumnos
                </button>
                <button
                  onClick={() => setView('calendario')}
                  className={`px-5 py-2 rounded-lg text-sm font-inter transition-colors flex items-center gap-2 ${
                    view === 'calendario'
                      ? 'bg-[#A3E635] text-bg-primary font-semibold'
                      : 'text-text-secondary hover:text-white'
                  }`}
                  data-testid="gym-view-calendario"
                >
                  <Calendar size={14} /> Calendario
                </button>
                <button
                  onClick={() => setView('revision')}
                  className={`px-5 py-2 rounded-lg text-sm font-inter transition-colors flex items-center gap-2 ${
                    view === 'revision'
                      ? 'bg-[#A3E635] text-bg-primary font-semibold'
                      : 'text-text-secondary hover:text-white'
                  }`}
                  data-testid="gym-view-revision"
                >
                  <ClipboardList size={14} /> Revisión
                </button>
              </div>

              {view === 'alumnos' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {alumnos.map((a) => {
                    const unplanned = unplanned48h.has(a.userId);
                    return (
                      <button
                        key={a.userId}
                        onClick={() => navigate(`/monitor/gym/${a.userId}`)}
                        data-testid={`gym-alumno-card-${a.userId}`}
                        data-unplanned={unplanned ? '1' : '0'}
                        className={`card text-left hover:border-[#A3E635]/40 transition-colors group flex items-center gap-4 ${
                          unplanned ? 'border-[#EF4444]/40' : ''
                        }`}
                      >
                        <div className="w-11 h-11 rounded-full bg-[#A3E635]/10 border border-[#A3E635]/30 flex items-center justify-center flex-shrink-0">
                          <span className="font-oswald text-lg text-[#A3E635]">
                            {a.fullName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-inter text-white font-medium text-sm truncate">
                            {a.fullName}
                          </p>
                          <p className="font-inter text-text-secondary text-xs mt-0.5">
                            Hasta: {formatExpiry(a.expiryDate)}
                          </p>
                          {unplanned && (
                            <span
                              data-testid={`gym-unplanned-badge-${a.userId}`}
                              className="inline-flex items-center gap-1 mt-1 text-[11px] font-inter text-[#EF4444]"
                            >
                              <AlertCircle size={11} /> Sin plan 48h
                            </span>
                          )}
                        </div>
                        {a.pendingVideos > 0 && (
                          <span className="flex items-center gap-1 bg-[#A3E635] text-bg-primary text-xs font-oswald font-bold px-2 py-1 rounded-full flex-shrink-0">
                            <Video size={11} /> {a.pendingVideos}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {view === 'calendario' && <MonthCalendar alumnos={alumnos} />}

              {view === 'revision' && profile && <WeeklyReviewView monitorId={profile.id} />}
            </>
          )}

          {/* CADUCADOS / RENOVAR — asignados sin bono vigente por fecha. No
              desaparecen: quedan aquí, separados de los activos, con su fecha de
              caducidad. Solo en la vista de alumnos. */}
          {caducados.length > 0 && view === 'alumnos' && (
            <div className="space-y-3" data-testid="gym-caducados-section">
              <div className="flex items-center gap-2 pt-2">
                <AlertCircle size={16} className="text-text-secondary" />
                <h3 className="font-oswald text-sm uppercase tracking-wider text-text-secondary">
                  Bono caducado — renovar
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {caducados.map((c) => (
                  <button
                    key={c.userId}
                    onClick={() => navigate(`/monitor/gym/${c.userId}`)}
                    data-testid={`gym-caducado-card-${c.userId}`}
                    className="card text-left opacity-70 hover:opacity-100 hover:border-[#EF4444]/40 transition-all flex items-center gap-4"
                  >
                    <div className="w-11 h-11 rounded-full bg-bg-elevated border border-bg-border flex items-center justify-center flex-shrink-0">
                      <span className="font-oswald text-lg text-text-secondary">
                        {c.fullName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-inter text-white font-medium text-sm truncate">
                        {c.fullName}
                      </p>
                      <p className="font-inter text-[#EF4444] text-xs mt-0.5">
                        Caducó: {formatExpiry(c.expiryDate)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Leyenda — §30: estados de día */}
      {view === 'calendario' && alumnos.length > 0 && (
        <div className="card p-4 flex flex-wrap items-center gap-4 text-xs font-inter text-text-secondary">
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" /> Completado
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" /> Parcial
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" /> Sin completar
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-bg-border" /> Descanso
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Calendar component ─────────────────────────────────────────────────────

const STATUS_COLOR: Record<DayStatus, string | null> = {
  green: 'bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/40',
  orange: 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/40',
  red: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/40',
  rest: 'bg-bg-border/40 text-text-secondary border-bg-border',
  none: null,
};

const DOW_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function MonthCalendar({ alumnos }: { alumnos: AlumnoCard[] }) {
  const today = new Date();
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  // Map[date → entries por alumno].
  const [byDay, setByDay] = useState<Record<string, CalendarEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [calendarioIlegible, setCalendarioIlegible] = useState(false);
  const todayStr = todayStrLocal();

  const loadMonth = useCallback(
    async (monthStart: Date) => {
      setLoading(true);
      const y = monthStart.getFullYear();
      const m = monthStart.getMonth();
      const daysInMonth = new Date(y, m + 1, 0).getDate();
      const dates = Array.from({ length: daysInMonth }, (_, i) => toDateStr(y, m, i + 1));

      // Para cada (alumno, día) pedimos el status. Es O(alumnos × días)
      // por mes ≈ 6×30 = 180 calls. Es aceptable para los volúmenes
      // del club hoy (mismo patrón usado por CalendarTab en
      // MonitorGymAlumnoPage). Si en el futuro hay >20 alumnos GYM
      // habría que abrir una RPC dedicada `gym_status_for_monitor_month`.
      const map: Record<string, CalendarEntry[]> = {};
      for (const d of dates) map[d] = [];

      await Promise.all(
        alumnos.flatMap((a) =>
          dates.map(async (date) => {
            const { data, error } = await supabase.rpc('gym_routine_status_for_day', {
              p_user_id: a.userId,
              p_date: date,
            });
            // Un fallo dejaba 'none', y un 'none' aquí no pinta al alumno en
            // ese día: desaparece del calendario sin dejar rastro.
            if (error) {
              setCalendarioIlegible(true);
              return;
            }
            // RPC devuelve text directo: 'green'|'orange'|'red'|'rest'|'none'.
            // El cliente lo recibe como string. Casteamos al tipo del
            // estado conocido y dejamos `none` como fallback.
            const status = ((typeof data === 'string' ? data : 'none') as DayStatus) ?? 'none';
            if (status !== 'none') {
              map[date].push({ userId: a.userId, fullName: a.fullName, status });
            }
          })
        )
      );

      // Orden estable por nombre para que la lista no salte entre renders.
      for (const k of Object.keys(map)) {
        map[k].sort((x, y) => x.fullName.localeCompare(y.fullName));
      }
      setByDay(map);
      setLoading(false);
    },
    [alumnos]
  );

  useEffect(() => {
    void loadMonth(current);
  }, [current, loadMonth]);

  const cells = useMemo(() => {
    const y = current.getFullYear();
    const m = current.getMonth();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const firstDow = dowMondayFirst(new Date(y, m, 1));
    const total = Math.ceil((firstDow + daysInMonth) / 7) * 7;
    return Array.from({ length: total }, (_, i) => {
      const day = i - firstDow + 1;
      return day >= 1 && day <= daysInMonth ? day : null;
    });
  }, [current]);

  const monthName = current.toLocaleString('es-ES', { month: 'long', year: 'numeric' });
  const y = current.getFullYear();
  const m = current.getMonth();

  return (
    <div className="card" data-testid="monitor-gym-calendar">
      {calendarioIlegible && (
        <p className="text-error text-sm mb-3" data-testid="gym-calendario-ilegible" role="status">
          No se ha podido leer el estado de algún día. Un día en blanco aquí no quiere decir que no
          haya rutina.
        </p>
      )}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCurrent(new Date(y, m - 1, 1))}
          className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-secondary"
          aria-label="Mes anterior"
          data-testid="gym-cal-prev"
        >
          <ChevronLeft size={18} />
        </button>
        <h3 className="font-oswald text-lg uppercase tracking-wider text-white">{monthName}</h3>
        <button
          onClick={() => setCurrent(new Date(y, m + 1, 1))}
          className="p-2 rounded-lg hover:bg-bg-elevated transition-colors text-text-secondary"
          aria-label="Mes siguiente"
          data-testid="gym-cal-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {loading && (
        <div className="text-center text-text-secondary text-sm font-inter py-6">Cargando…</div>
      )}

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DOW_LABELS.map((l) => (
          <div
            key={l}
            className="text-text-secondary font-inter text-xs text-center uppercase tracking-wider py-1"
          >
            {l}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`empty-${idx}`} />;
          const dateStr = toDateStr(y, m, day);
          const entries = byDay[dateStr] ?? [];
          const isToday = dateStr === todayStr;
          return (
            <div
              key={dateStr}
              className={`border rounded-lg p-1.5 min-h-[72px] flex flex-col gap-1 ${
                isToday ? 'border-[#A3E635]/50 bg-[#A3E635]/5' : 'border-bg-border bg-bg-surface'
              }`}
              data-testid={`gym-cal-day-${dateStr}`}
              data-day-count={entries.length}
            >
              <div
                className={`font-oswald text-xs ${
                  isToday ? 'text-[#A3E635]' : 'text-text-secondary'
                }`}
              >
                {day}
              </div>
              <div className="flex flex-col gap-0.5">
                {entries.map((e) => {
                  const cls = STATUS_COLOR[e.status];
                  if (!cls) return null;
                  return (
                    <span
                      key={`${dateStr}-${e.userId}`}
                      className={`text-[10px] leading-tight font-inter border rounded px-1 py-0.5 truncate ${cls}`}
                      title={`${e.fullName} — ${e.status}`}
                      data-testid={`gym-cal-entry-${dateStr}-${e.userId}`}
                      data-status={e.status}
                    >
                      {e.fullName}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Weekly review (§37) ────────────────────────────────────────────────────
//
// Vista semanal de "Revisión" para el trainer: lista, por día de la semana,
// los alumnos cuya rutina ya ha sido ejecutada — diferenciando si el trainer
// ya la corrigió (verde, status='reviewed') o si sigue sin corregir (rojo,
// status='pending'). Los días `none`/`rest`/`no_session` no se muestran
// porque no entran en el flujo de revisión.
//
// La RPC `gym_monitor_review_week` agrega los 7 días para todos los alumnos
// asignados al monitor — alternativa más eficiente al patrón N×7 client-side
// que usa `MonthCalendar`. Coherente con la promesa del brief PR-G5: vista
// compacta, click en un nombre → detalle de la rutina del día para
// corregir/revisar (deep-link `?date=YYYY-MM-DD` resuelto por RevisionTab).

const REVIEW_DOW_LABELS = [
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
];

const REVIEW_PILL: Record<ReviewStatus, { cls: string; label: string }> = {
  reviewed: {
    cls: 'bg-[#22C55E]/20 text-[#22C55E] border-[#22C55E]/40',
    label: 'Corregida',
  },
  pending: {
    cls: 'bg-[#EF4444]/20 text-[#EF4444] border-[#EF4444]/40',
    label: 'Sin corregir',
  },
};

// Lunes-primero igual que MonthCalendar para coherencia visual.
function mondayOfWeek(d: Date): Date {
  const day = (d.getDay() + 6) % 7;
  const out = new Date(d);
  out.setDate(d.getDate() - day);
  out.setHours(0, 0, 0, 0);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(d.getDate() + n);
  return out;
}

function formatWeekRange(start: Date, end: Date): string {
  const fmt = (x: Date) => x.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  return `${fmt(start)} – ${fmt(end)} ${end.getFullYear()}`;
}

interface ReviewWeekRow {
  date: string;
  userId: string;
  fullName: string;
  status: ReviewStatus;
}

function WeeklyReviewView({ monitorId }: { monitorId: string }) {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ReviewWeekRow[]>([]);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const weekStartStr = useMemo(
    () => toDateStr(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()),
    [weekStart]
  );

  useEffect(() => {
    setLoading(true);
    void supabase
      .rpc('gym_monitor_review_week', {
        p_monitor_id: monitorId,
        p_week_start: weekStartStr,
      })
      .then(({ data, error }) => {
        if (error || !data) {
          setRows([]);
        } else {
          // Tipo: la RPC devuelve { date, user_id, full_name, status }[]
          // declarado en database.types.ts (PR-G5).
          const mapped: ReviewWeekRow[] = (
            data as { date: string; user_id: string; full_name: string; status: string }[]
          )
            .filter((r) => r.status === 'reviewed' || r.status === 'pending')
            .map((r) => ({
              date: r.date,
              userId: r.user_id,
              fullName: r.full_name,
              status: r.status as ReviewStatus,
            }));
          setRows(mapped);
        }
        setLoading(false);
      });
  }, [monitorId, weekStartStr]);

  // Agrupa por día de la semana (0=Lunes..6=Domingo). Calculamos el offset
  // sobre días UTC absolutos para que la operación no se vea afectada por
  // zona horaria ni cambios DST dentro de la semana.
  const byDay = useMemo(() => {
    const map: Record<number, ReviewWeekRow[]> = {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
    };
    const dayOf = (s: string) => {
      const [y, m, d] = s.split('-').map(Number);
      return Math.floor(Date.UTC(y, m - 1, d) / (1000 * 60 * 60 * 24));
    };
    const start = dayOf(weekStartStr);
    for (const r of rows) {
      const offset = dayOf(r.date) - start;
      if (offset >= 0 && offset <= 6) map[offset].push(r);
    }
    return map;
  }, [rows, weekStartStr]);

  const totalPending = rows.filter((r) => r.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="gym-revision-week-view">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="w-9 h-9 rounded-lg bg-bg-surface border border-bg-border flex items-center justify-center hover:border-[#A3E635]/40 transition-colors"
          data-testid="gym-revision-week-prev"
          aria-label="Semana anterior"
        >
          <ChevronLeft size={16} className="text-text-secondary" />
        </button>
        <div className="text-center">
          <p className="font-inter text-sm text-white capitalize">
            {formatWeekRange(weekStart, weekEnd)}
          </p>
          <p className="font-inter text-xs text-text-secondary mt-0.5">
            {totalPending > 0
              ? `${totalPending} rutina${totalPending !== 1 ? 's' : ''} pendiente${totalPending !== 1 ? 's' : ''} de revisar`
              : 'Sin rutinas pendientes'}
          </p>
        </div>
        <button
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="w-9 h-9 rounded-lg bg-bg-surface border border-bg-border flex items-center justify-center hover:border-[#A3E635]/40 transition-colors"
          data-testid="gym-revision-week-next"
          aria-label="Semana siguiente"
        >
          <ChevronRight size={16} className="text-text-secondary" />
        </button>
      </div>

      {loading ? (
        <div className="card text-center text-text-secondary text-sm font-inter py-8">
          Cargando…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="card text-center text-text-secondary text-sm font-inter py-8"
          data-testid="gym-revision-week-empty"
        >
          No hay rutinas ejecutadas esta semana.
        </div>
      ) : (
        <div className="space-y-2">
          {REVIEW_DOW_LABELS.map((label, i) => {
            const dayRows = byDay[i];
            const dayDate = addDays(weekStart, i);
            const dayStr = toDateStr(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
            if (dayRows.length === 0) return null;
            return (
              <div
                key={dayStr}
                className="card p-4"
                data-testid={`gym-revision-week-day-${dayStr}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-oswald text-sm uppercase tracking-wider text-white">
                    {label}{' '}
                    <span className="text-text-secondary font-inter text-xs normal-case ml-1">
                      {dayDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    </span>
                  </h4>
                  <span className="font-inter text-xs text-text-secondary">
                    {dayRows.length} alumno{dayRows.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <ul className="flex flex-col gap-1.5">
                  {dayRows.map((r) => {
                    const pill = REVIEW_PILL[r.status];
                    const Icon = r.status === 'reviewed' ? CheckCircle2 : AlertCircle;
                    return (
                      <li key={`${dayStr}-${r.userId}`}>
                        <button
                          onClick={() =>
                            navigate(`/monitor/gym/${r.userId}?tab=revision&date=${dayStr}`)
                          }
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-bg-surface border border-bg-border hover:border-[#A3E635]/40 transition-colors text-left"
                          data-testid={`gym-revision-week-row-${dayStr}-${r.userId}`}
                          data-status={r.status}
                        >
                          <span className="font-inter text-sm text-white truncate">
                            {r.fullName}
                          </span>
                          <span
                            className={`flex items-center gap-1.5 text-[11px] font-inter border rounded-full px-2 py-0.5 flex-shrink-0 ${pill.cls}`}
                          >
                            <Icon size={11} />
                            {pill.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Leyenda */}
      <div className="card p-3 flex flex-wrap items-center gap-4 text-xs font-inter text-text-secondary">
        <span className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#22C55E]" /> Corregida por el trainer
        </span>
        <span className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]" /> Sin corregir
        </span>
      </div>
    </div>
  );
}
